import React, { useContext } from 'react';
import connectToStores from 'fluxible-addons-react/connectToStores';
import PropTypes from 'prop-types';
import { Link } from 'found';
import {
  createFragmentContainer,
  graphql,
  QueryRenderer,
  ReactRelayContext,
} from 'react-relay';
import moment from 'moment';
import MarkerPopupBottom from '../MarkerPopupBottom';
import PopupHeader from '../PopupHeader';
import Card from '../../Card';
import { PREFIX_STOPS } from '../../../util/path';
import Loading from '../../Loading';
import DepartureListContainer from '../../DepartureListContainer';
import AddressRow from '../../AddressRow';

const NUMBER_OF_DEPARTURES = 5;
const STOP_TIME_RANGE = 12 * 60 * 60;

class StopMarkerPopupContentInner extends React.Component {
  static propTypes = {
    stop: PropTypes.object.isRequired,
    locationPopup: PropTypes.string,
    onSelectLocation: PropTypes.func,
    lon: PropTypes.number.isRequired,
    lat: PropTypes.number.isRequired,
    currentTime: PropTypes.instanceOf(moment).isRequired,
  };

  render() {
    const { stop, currentTime } = this.props;
    const stopLink = `/${PREFIX_STOPS}/${encodeURIComponent(stop.gtfsId)}`;

    return (
      <Card className="stop-marker-popup">
        <PopupHeader
          header={
            <span className="header-primary no-margin link-color">
              <Link to={stopLink}>
                {stop.name} <span className="link-arrow"> ›</span>
              </Link>
            </span>
          }
          subHeader={<AddressRow desc={stop.desc} code={stop.code} />}
        />
        <DepartureListContainer
          className="stop-page"
          stoptimes={stop.stoptimes}
          limit={NUMBER_OF_DEPARTURES}
          currentTime={currentTime}
          showPlatformCodes
          isPopup
        />
        {(this.props.locationPopup === 'all' ||
          this.props.locationPopup === 'origindestination') && (
          <MarkerPopupBottom
            location={{
              address: stop.name,
              lat: this.props.lat,
              lon: this.props.lon,
            }}
            locationPopup={this.props.locationPopup}
            onSelectLocation={this.props.onSelectLocation}
          />
        )}
      </Card>
    );
  }
}

const StopMarkerPopupContent = createFragmentContainer(
  StopMarkerPopupContentInner,
  {
    stop: graphql`
      fragment StopMarkerPopup_stop on Stop
      @argumentDefinitions(
        startTime: { type: "Long!" }
        timeRange: { type: "Int!" }
        numberOfDepartures: { type: "Int!" }
      ) {
        gtfsId
        lat
        lon
        name
        desc
        code
        stoptimes: stoptimesWithoutPatterns(
          startTime: $startTime
          timeRange: $timeRange
          numberOfDepartures: $numberOfDepartures
          omitCanceled: false
        ) {
          ...DepartureListContainer_stoptimes
        }
      }
    `,
  },
);

// eslint-disable-next-line react/prop-types
const StopMarkerPopup = props => {
  const { stopId, currentTime } = props;
  const { environment } = useContext(ReactRelayContext);
  return (
    <QueryRenderer
      query={graphql`
        query StopMarkerPopupQuery(
          $stopId: String!
          $startTime: Long!
          $timeRange: Int!
          $numberOfDepartures: Int!
        ) {
          stop(id: $stopId) {
            ...StopMarkerPopup_stop
            @arguments(
              startTime: $startTime
              timeRange: $timeRange
              numberOfDepartures: $numberOfDepartures
            )
          }
        }
      `}
      variables={{
        stopId,
        startTime: currentTime,
        timeRange: STOP_TIME_RANGE,
        numberOfDepartures: NUMBER_OF_DEPARTURES,
      }}
      environment={environment}
      render={({ props: innerProps }) =>
        innerProps ? (
          <StopMarkerPopupContent {...innerProps} {...props} />
        ) : (
          <Loading />
        )
      }
    />
  );
};

StopMarkerPopup.propTypes = {
  stopId: PropTypes.string.isRequired,
  currentTime: PropTypes.instanceOf(moment).isRequired,
};

const connectedComponent = connectToStores(
  StopMarkerPopup,
  ['TimeStore'],
  context => ({
    currentTime: context.getStore('TimeStore').getCurrentTime().unix(),
  }),
);

export { connectedComponent as default, StopMarkerPopup as Component };
